import dayjs from 'dayjs';
import JSZip from 'jszip';

export const BULK_PDF_EXPORT_LIMIT = 50;

interface BulkPdfExportRecord {
  id: string;
  document?: { url: string; name: string } | null;
}

export async function exportRecordsToPdfZip(
  records: BulkPdfExportRecord[],
  filenamePrefix: string,
  onProgress?: (completed: number, total: number) => void,
): Promise<void> {
  const recordsWithPdf = records.filter((record): record is BulkPdfExportRecord & { document: { url: string; name: string } } => Boolean(record.document?.url));

  if (recordsWithPdf.length === 0) {
    throw new Error('No PDF documents available for the selected records');
  }

  if (recordsWithPdf.length > BULK_PDF_EXPORT_LIMIT) {
    throw new Error(`Maximum ${BULK_PDF_EXPORT_LIMIT} records with PDFs can be exported at once`);
  }

  const zip = new JSZip();
  let completed = 0;

  // Pre-assign unique filenames to avoid collisions when documents share the same name
  const usedFilenames = new Map<string, number>();
  const filenames = recordsWithPdf.map((record) => {
    const baseName = record.document.name || `${record.id}.pdf`;
    const count = usedFilenames.get(baseName) ?? 0;
    usedFilenames.set(baseName, count + 1);
    return count === 0 ? baseName : baseName.replace(/\.pdf$/i, `-${count}.pdf`);
  });

  const results = await Promise.allSettled(
    recordsWithPdf.map(async (record, index) => {
      const response = await fetch(record.document.url);
      if (!response.ok) throw new Error(`Failed to fetch PDF for record ${record.id}`);
      const blob = await response.blob();
      zip.file(filenames[index], blob);
      completed++;
      onProgress?.(completed, recordsWithPdf.length);
    }),
  );

  const failures = results.filter((result) => result.status === 'rejected');
  if (failures.length === recordsWithPdf.length) {
    throw new Error('Failed to download all PDFs');
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' });
  triggerBlobDownload(zipBlob, `${filenamePrefix}-${dayjs().format('YYYY-MM-DD')}.zip`);
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = Object.assign(document.createElement('a'), {
    href: url,
    download: filename,
    style: 'visibility:hidden',
  });
  document.body.appendChild(link);
  link.click();
  link.remove();
}
